import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PageContainer from '../components/PageContainer';
import DashboardQuickGuide from '../components/DashboardQuickGuide';
import DailyStreakCard from '../components/DailyStreakCard';
import ActivityFeed from '../components/ActivityFeed';
import FeaturedHighlightCard from '../components/FeaturedHighlightCard';
import LiveFeedPopup from '../components/LiveFeedPopup';
import { CODE_ICON, actionIcons } from '../utils/iconMap';
import StatCard from '../components/ds/StatCard';
import ActionButton from '../components/ds/ActionButton';
import StateSurface from '../components/ds/StateSurface';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  RARITY_LABELS_TR,
  RARITY_UI,
  revealRarityKey,
  parseTs,
  collectorTitleFromScore,
} from '../utils/collectionUi';
import { usePagePerf, markPagePerfDataLoaded } from '../hooks/usePagePerf';
import { getApiBase } from '../utils/apiBase';
import { getActivityFeedHighlightsCached } from '../utils/activityFeedCachedClient';
import { t } from '../locales';

const API_URL = getApiBase() + '/api';

const LayoutDashboardIcon = actionIcons.layoutDashboard;
const SparklesIcon = actionIcons.sparkles;

function DashboardDataSkeleton() {
  return (
    <div className="space-y-6">
      <div className="glass-effect rounded-2xl p-6 border border-white/10 animate-pulse">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="space-y-2 min-w-0 flex-1">
            <div className="h-4 w-32 rounded bg-zinc-800/80" />
            <div className="h-10 w-40 rounded bg-zinc-800/90" />
            <div className="h-3 w-52 rounded bg-zinc-800/70" />
          </div>
          <div className="grid grid-cols-2 gap-3 w-full sm:w-auto sm:min-w-[14rem]">
            <div className="h-20 rounded-xl bg-zinc-900/80 border border-white/10" />
            <div className="h-20 rounded-xl bg-zinc-900/80 border border-white/10" />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="h-6 w-44 rounded bg-zinc-800/80 animate-pulse" />
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={`rarity-sk-${i}`} className="glass-effect rounded-xl p-3 border border-white/10 animate-pulse">
              <div className="h-3 w-16 rounded bg-zinc-800/80" />
              <div className="h-6 w-10 rounded bg-zinc-800/90 mt-2" />
              <div className="h-2.5 w-12 rounded bg-zinc-800/70 mt-2" />
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <div className="h-6 w-36 rounded bg-zinc-800/80 animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={`recent-sk-${i}`} className="glass-effect rounded-xl overflow-hidden border border-white/10 animate-pulse">
              <div className="aspect-square bg-zinc-900/80" />
              <div className="p-2 space-y-1.5">
                <div className="h-3 w-4/5 rounded bg-zinc-800/80" />
                <div className="h-2.5 w-1/2 rounded bg-zinc-800/70" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const Dashboard = () => {
  usePagePerf('dashboard', {});
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scorePayload, setScorePayload] = useState(null);
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [dashboardReady, setDashboardReady] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);
  /** null = yükleniyor; dizi = highlights (öne çıkan kart + aktivite akışı) */
  const [feedHighlights, setFeedHighlights] = useState(null);

  useEffect(() => {
    const load = async () => {
      setDashboardReady(false);
      setDashboardError(null);
      try {
        const [scoreRes, profRes, colRes, invRes] = await Promise.all([
          axios.get(`${API_URL}/me/collection-score`, { withCredentials: true }),
          axios.get(`${API_URL}/user/profile`, { withCredentials: true }),
          axios.get(`${API_URL}/me/collection`, { withCredentials: true }),
          axios.get(`${API_URL}/user/inventory`, { withCredentials: true }),
        ]);
        setScorePayload(scoreRes.data);
        setStats(profRes.data?.stats || { total_characters: 0, total_items: 0 });
        setItems(Array.isArray(colRes.data?.items) ? colRes.data.items : []);
        setCharacters(Array.isArray(invRes.data?.characters) ? invRes.data.characters : []);
      } catch (e) {
        console.error(e);
        setDashboardError(t('common.dashboard.loadError'));
      } finally {
        setDashboardReady(true);
        markPagePerfDataLoaded();
      }
    };
    load();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resData = await getActivityFeedHighlightsCached(28);
        if (!cancelled) {
          setFeedHighlights(Array.isArray(resData?.events) ? resData.events : []);
        }
      } catch {
        if (!cancelled) setFeedHighlights([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const totalScore = scorePayload?.total_score ?? 0;
  const title = collectorTitleFromScore(totalScore);
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const nextClanTarget = totalScore < 300 ? 300 : totalScore < 1000 ? 1000 : null;

  const rarityOrder = ['common', 'rare', 'epic', 'legendary', 'mythic'];

  const recentAcquisitions = useMemo(() => {
    const rows = [];
    items.forEach((it) => {
      rows.push({
        kind: 'item',
        id: it.user_item_id || it.item_id,
        sort: parseTs(it.acquired_at),
        name: it.name,
        image: it.image,
        rarity: it.rarity,
        href: `/items/${it.item_id}`,
        state: { userItemId: it.user_item_id },
      });
    });
    characters.forEach((uc) => {
      const ch = uc.character;
      if (!ch) return;
      rows.push({
        kind: 'character',
        id: uc.user_character_id || ch.character_id,
        sort: parseTs(uc.acquired_at),
        name: ch.name,
        image: ch.image_url || ch.image,
        rarity: ch.rarity,
        href: `/character/${ch.character_id}`,
        state: undefined,
      });
    });
    rows.sort((a, b) => b.sort - a.sort);
    return rows.slice(0, 6);
  }, [items, characters]);

  const scoreAside =
    dashboardReady && !dashboardError ? (
      <>
        <div className="rounded-xl border border-white/10 bg-black/20 p-3 max-w-md lg:max-w-none min-w-0 w-full">
          <div className="text-xs text-zinc-500">{t('common.dashboard.mainMetric')}</div>
          <div className="text-sm text-zinc-200 mt-1">
            {t('common.dashboard.collectionScoreLabel')}{' '}
            <span className="font-semibold tabular-nums text-violet-200">{totalScore}</span>
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">{t('common.dashboard.growHint')}</div>
          <div className="text-[10px] text-zinc-600/80 mt-2 border-t border-white/5 pt-1.5">
            {nextClanTarget == null
              ? t('common.dashboard.clanUnlocked1000')
              : nextClanTarget === 300
                ? t('common.dashboard.clanUnlock300')
                : t('common.dashboard.clanUnlock1000')}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="glass-effect rounded-2xl p-6 border border-violet-500/25 opacity-80"
        >
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <p className="text-gray-400 text-sm mb-1 flex items-center gap-2">
                <SparklesIcon className="w-4 h-4 text-violet-400" />
                {t('common.dashboard.sectionScoreTitle')}
              </p>
              <p
                className="text-4xl font-black bg-gradient-to-r from-violet-400 via-white to-amber-300 bg-clip-text text-transparent"
                data-testid="dashboard-collection-score"
              >
                {totalScore}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                {t('common.dashboard.scoreFootnote', { title })}
              </p>
              <p className="text-[11px] text-gray-600 mt-2 leading-snug max-w-xl lg:max-w-none">
                {t('common.dashboard.scoreDescription')}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 text-sm">
              <StatCard
                label={t('common.dashboard.statCharacter')}
                value={stats.total_characters}
                colorKey="users"
                className="!p-3 !rounded-xl"
                valueClassName="text-2xl font-bold text-white"
                index={0}
              />
            </div>
          </div>

          {scorePayload?.breakdown && (
            <div className="mt-4 flex flex-wrap gap-2 text-xs text-gray-500">
              <span>
                {t('common.dashboard.breakdownCharacter')} {scorePayload.breakdown.characters}
              </span>
            </div>
          )}
        </motion.div>
      </>
    ) : null;

  return (
    <div className="bg-background">
      <LiveFeedPopup />
      <div className="glass-effect border-b border-white/10 p-4">
        <PageContainer variant="wide" className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-black text-white" style={{ fontFamily: 'Cinzel, serif' }}>
              {t('common.brand')}
            </h1>
            <p className="text-sm text-gray-400">
              {t('common.dashboard.welcome', { name: user?.name || t('common.playerFallback') })}
            </p>
          </div>
          {isAdmin && (
            <ActionButton
              type="button"
              onClick={() => navigate('/admin')}
              className="px-4 py-2.5 rounded-xl bg-violet-600/90 hover:bg-violet-500 text-white text-sm font-semibold border border-violet-400/30 shrink-0 w-full sm:w-auto justify-center"
              data-testid="dashboard-admin-entry"
              icon={LayoutDashboardIcon}
              iconClassName="w-4 h-4"
            >
              {t('common.adminPanel')}
            </ActionButton>
          )}
        </PageContainer>
      </div>

      <PageContainer variant="wide" className="py-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8 lg:items-start">
          <div className="space-y-8 lg:col-span-8">
        <div className="space-y-3">
        {/* Hero Section - Primary Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-effect rounded-3xl p-5 sm:p-8 border border-violet-500/30 bg-gradient-to-br from-violet-950/20 via-black/50 to-violet-950/10 relative overflow-hidden"
        >
          {/* Background glow effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-violet-600/10 via-transparent to-violet-600/10 opacity-50" />
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-violet-400/50 to-transparent" />
          
          <div className="relative z-10 text-center max-w-2xl mx-auto">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.3 }}
            >
              <CODE_ICON className="w-16 h-16 mx-auto mb-6 text-violet-400 drop-shadow-[0_0_20px_rgba(139,92,246,0.4)]" />
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-3xl sm:text-4xl font-black text-white mb-4"
              style={{ fontFamily: 'Cinzel, serif' }}
            >
              {t('activation.heroTitle')}
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-lg text-gray-300 mb-8 leading-relaxed"
            >
              {t('activation.heroSubtitle')}
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="relative overflow-hidden group rounded-2xl"
            >
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out" />
              <ActionButton
                type="button"
                onClick={() => navigate('/activate')}
                className="relative z-10 px-12 py-4 bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white font-bold text-lg rounded-2xl border border-violet-400/30 shadow-[0_0_30px_rgba(139,92,246,0.3)] hover:shadow-[0_0_40px_rgba(139,92,246,0.4)] transition-all duration-300 w-full sm:w-auto"
                icon={CODE_ICON}
                iconClassName="w-5 h-5"
              >
                {t('activation.enterCode')}
              </ActionButton>
            </motion.div>
          </div>
        </motion.div>

        <DailyStreakCard />

        <DashboardQuickGuide />
        </div>

        <div className="space-y-4 lg:hidden">{scoreAside}</div>

        <FeaturedHighlightCard sharedHighlights={feedHighlights} />

        {/* Koleksiyon skoru - secondary section */}
        {!dashboardReady ? (
          <DashboardDataSkeleton />
        ) : dashboardError ? (
          <StateSurface variant="error" description={dashboardError} className="!text-left border-red-500/25 bg-red-950/15" />
        ) : (
          <>
            <ActivityFeed sharedHighlights={feedHighlights} />

            {/* Nadirlik dağılımı */}
            <div>
              <h2 className="text-lg font-bold text-white mb-3" style={{ fontFamily: 'Cinzel, serif' }}>
                {t('common.dashboard.raritySection')}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {rarityOrder.map((r) => {
                  const ui = RARITY_UI[r] || RARITY_UI.common;
                  const dist = scorePayload?.rarity_distribution?.[r];
                  const count = dist?.count ?? 0;
                  return (
                    <div
                      key={r}
                      className={`glass-effect rounded-xl p-3 border ${ui.border}`}
                    >
                      <div className={`text-xs font-semibold ${ui.text}`}>{RARITY_LABELS_TR[r]}</div>
                      <div className="text-xl font-bold text-white mt-1">{count}</div>
                      <div className="text-[10px] text-gray-600">
                        {t('common.dashboard.scoreLabel')} {dist?.score ?? 0}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Son kazanılanlar */}
            {recentAcquisitions.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Cinzel, serif' }}>
                    {t('common.dashboard.recentTitle')}
                  </h2>
                  <ActionButton
                    type="button"
                    variant="ghost"
                    onClick={() => navigate('/collection')}
                    className="text-violet-400 hover:text-violet-300 text-sm font-medium h-auto px-2 py-1"
                  >
                    {t('common.dashboard.goCollection')}
                  </ActionButton>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {recentAcquisitions.map((row, index) => {
                    const rk = revealRarityKey(row.rarity);
                    const ui = RARITY_UI[rk] || RARITY_UI.common;
                    return (
                      <motion.button
                        key={`${row.kind}-${row.id}`}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.04 }}
                        onClick={() => navigate(row.href, { state: row.state })}
                        className={`glass-effect rounded-xl overflow-hidden border text-left hover:border-violet-500/40 ${ui.border}`}
                      >
                        <div className="aspect-square bg-black/40 flex items-center justify-center p-2">
                          {row.image ? (
                            <img src={row.image} alt="" className="w-full h-full object-contain" />
                          ) : (
                            <span className="text-3xl text-gray-600">?</span>
                          )}
                        </div>
                        <div className="p-2">
                          <p className="text-white font-medium truncate text-sm">{row.name}</p>
                          <p className="text-[10px] text-gray-500 uppercase">
                            {row.kind === 'character'
                              ? t('common.dashboard.kindCharacter')
                              : t('common.dashboard.kindItem')}
                          </p>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded mt-1 inline-block ${ui.badge}`}>
                            {RARITY_LABELS_TR[rk]}
                          </span>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            )}

            {stats.total_items === 0 && stats.total_characters === 0 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <StateSurface
                  variant="empty"
                  icon={CODE_ICON}
                  title={t('activation.emptyCollection.title')}
                  description={t('activation.emptyCollection.hint')}
                  className="p-6 opacity-90 border-violet-500/20"
                >
                  <ActionButton
                    type="button"
                    className="mt-4 bg-violet-600 hover:bg-violet-500 text-white"
                    onClick={() => navigate('/activate')}
                  >
                    {t('activation.enterCode')}
                  </ActionButton>
                </StateSurface>
              </motion.div>
            )}
          </>
        )}
          </div>

          <aside className="hidden lg:block space-y-4 lg:col-span-4 lg:sticky lg:top-24 lg:self-start shrink-0">
            {scoreAside}
          </aside>
        </div>
      </PageContainer>
    </div>
  );
};

export default Dashboard;
